import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'dangerOutline' | 'ghost' | 'primarySoft';
type ButtonSize = 'sm' | 'md' | 'icon' | 'auto';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  'data-testid'?: string;
}

const variantConfig: Record<
  ButtonVariant,
  { className: string }
> = {
  primary: {
    className: 'border border-transparent bg-primary !text-primary-foreground shadow-none hover:bg-primary/90',
  },
  secondary: {
    className: 'border border-solid border-btn-secondary-border bg-card !text-foreground shadow-none hover:bg-muted/20',
  },
  danger: {
    className: 'border border-transparent bg-destructive !text-destructive-foreground shadow-none hover:opacity-90',
  },
  dangerOutline: {
    className: 'border border-destructive bg-transparent !text-destructive shadow-none hover:bg-destructive/10',
  },
  ghost: {
    className: 'border border-border bg-transparent !text-foreground shadow-none hover:bg-muted',
  },
  primarySoft: {
    className:
      'border border-transparent bg-[#d9e4f2] !text-[#0f172a] shadow-none hover:bg-[#cddbec]',
  },
};

const sizeConfig: Record<
  ButtonSize,
  { className: string }
> = {
  sm: {
    className: '',
  },
  md: {
    className: '',
  },
  icon: {
    className: 'h-btn-icon w-btn-icon p-0',
  },
  auto: {
    className: '',
  },
};

const Button = ({
  className,
  variant = 'primary',
  size = 'md',
  type = 'button',
  leftIcon,
  children,
  ...props
}: ButtonProps) => {
  return (
    <button
      data-th-button="true"
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-btn-gap whitespace-nowrap rounded-md border !text-[14px] !font-medium px-[16px] py-[8px] min-h-[44px] max-h-[44px] transition-colors appearance-none [-webkit-appearance:none] [background-image:none] [box-shadow:none] shadow-none ring-0 outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none active:translate-y-0 disabled:pointer-events-none disabled:opacity-50',
        variantConfig[variant].className,
        sizeConfig[size].className,
        className,
      )}
      {...props}
    >
      {leftIcon}
      {children}
    </button>
  );
};

export default Button;
